import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { Decoration, DecorationSet } from "prosemirror-view";
import { mergeBlocksIntoColumns } from "./mergeColumns";

export const columnDndKey = new PluginKey<DndState>("lumirColumnDnd");

type DndState = {
  /** 대상 top-level blockContainer의 pos(없으면 null). */
  targetPos: number | null;
  side: "left" | "right" | null;
};

/** 가장자리로 인식할 폭(px). 블록 폭의 30%와 80px 중 작은 값. */
function edgeThreshold(width: number): number {
  return Math.min(80, width * 0.3);
}

/**
 * 좌표 아래의 **top-level** blockContainer(루트 blockGroup 직속)를 찾는다.
 * v1은 최상위 블록만 컬럼 생성 대상으로 허용(컬럼 내부/중첩 블록은 제외).
 */
function topLevelBlockAtCoords(
  view: EditorView,
  x: number,
  y: number,
): { pos: number; nodeSize: number; id: string; dom: HTMLElement } | null {
  const found = view.posAtCoords({ left: x, top: y });
  if (!found) {
    return null;
  }
  const $pos = view.state.doc.resolve(found.pos);
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === "blockContainer") {
      // 루트 blockGroup(depth 1) 직속만 → blockContainer는 depth 2.
      if (d !== 2 || $pos.node(d - 1).type.name !== "blockGroup") {
        return null;
      }
      const pos = $pos.before(d);
      const node = $pos.node(d);
      const dom = view.nodeDOM(pos) as HTMLElement | null;
      if (!dom || !node.attrs?.id) {
        return null;
      }
      return { pos, nodeSize: node.nodeSize, id: node.attrs.id, dom };
    }
  }
  return null;
}

/** NodeSelection이면 드래그된 단일 블록 id를 반환(다중 선택은 v1 미지원 → null). */
function draggedBlockId(view: EditorView): string | null {
  const sel: any = view.state.selection;
  // MultipleNodeSelection 등은 node가 없음 → v1 단일만.
  return sel?.node?.attrs?.id ?? null;
}

function setDnd(view: EditorView, next: DndState) {
  const cur = columnDndKey.getState(view.state);
  if (cur && cur.targetPos === next.targetPos && cur.side === next.side) {
    return; // 변화 없음 → dispatch 생략(드래그오버 스팸 방지)
  }
  view.dispatch(view.state.tr.setMeta(columnDndKey, next));
}

const CLEARED: DndState = { targetPos: null, side: null };

/**
 * 블록을 다른 블록의 좌/우 가장자리로 드롭하면 2단 컬럼을 생성하는 DnD 플러그인.
 * - dragover: 대상 블록의 좌/우 가장자리 hit-test → 세로 인디케이터(데코) + 기본 드롭커서 숨김.
 * - drop(handleDrop): 가장자리 드롭이면 mergeBlocksIntoColumns로 래핑하고 기본 동작 취소.
 */
export function columnDnd(): Plugin<DndState> {
  return new Plugin<DndState>({
    key: columnDndKey,
    state: {
      init: () => CLEARED,
      apply(tr, prev) {
        const meta = tr.getMeta(columnDndKey);
        if (meta) {
          return meta as DndState;
        }
        // 문서가 바뀌면 좌표 기반 targetPos가 무효 → 초기화.
        if (tr.docChanged && prev.targetPos !== null) {
          return CLEARED;
        }
        return prev;
      },
    },
    props: {
      handleDOMEvents: {
        dragover: (view, event) => {
          if (!(view as any).dragging) {
            return false;
          }
          const target = topLevelBlockAtCoords(
            view,
            event.clientX,
            event.clientY,
          );
          const draggedId = draggedBlockId(view);
          if (!target || !draggedId || target.id === draggedId) {
            setDnd(view, CLEARED);
            view.dom.classList.remove("lumir-col-dnd-active");
            return false;
          }
          const rect = target.dom.getBoundingClientRect();
          const th = edgeThreshold(rect.width);
          let side: "left" | "right" | null = null;
          if (event.clientX - rect.left <= th) {
            side = "left";
          } else if (rect.right - event.clientX <= th) {
            side = "right";
          }
          if (side) {
            setDnd(view, { targetPos: target.pos, side });
            view.dom.classList.add("lumir-col-dnd-active");
          } else {
            setDnd(view, CLEARED);
            view.dom.classList.remove("lumir-col-dnd-active");
          }
          return false;
        },
        dragend: (view) => {
          setDnd(view, CLEARED);
          view.dom.classList.remove("lumir-col-dnd-active");
          return false;
        },
        dragleave: (view, event) => {
          // 에디터 밖으로 나가면 정리(내부 이동은 무시).
          if (!(event as DragEvent).relatedTarget) {
            setDnd(view, CLEARED);
            view.dom.classList.remove("lumir-col-dnd-active");
          }
          return false;
        },
      },
      handleDrop: (view, _event, _slice, _moved) => {
        const st = columnDndKey.getState(view.state);
        view.dom.classList.remove("lumir-col-dnd-active");
        if (!st || st.side === null || st.targetPos === null) {
          return false; // 일반(상하) 드롭 → 기본 처리
        }
        const draggedId = draggedBlockId(view);
        const targetNode = view.state.doc.nodeAt(st.targetPos);
        const targetId = targetNode?.attrs?.id as string | undefined;
        // 상태 초기화(드롭 후 잔여 인디케이터 제거)
        view.dispatch(view.state.tr.setMeta(columnDndKey, CLEARED));
        if (!draggedId || !targetId) {
          return false;
        }
        const editor = (view as any).__lumirEditor;
        if (!editor) {
          return false;
        }
        const ok = mergeBlocksIntoColumns(editor, draggedId, targetId, st.side);
        return ok; // 성공 시 기본 드롭 취소
      },
      decorations: (state) => {
        const st = columnDndKey.getState(state);
        if (!st || st.side === null || st.targetPos === null) {
          return null;
        }
        const node = state.doc.nodeAt(st.targetPos);
        if (!node) {
          return null;
        }
        return DecorationSet.create(state.doc, [
          Decoration.node(st.targetPos, st.targetPos + node.nodeSize, {
            class:
              st.side === "left"
                ? "lumir-col-drop-left"
                : "lumir-col-drop-right",
          }),
        ]);
      },
    },
  });
}
